
import React, { useState } from 'react';
import { useParams } from "react-router-dom";
import { useApiReadStudentLessonDetailRaw } from "@/api/lesson";
import { URLS } from "@/lib/urls";
import RenderIf from "@/components/render-if";
import { Button, Spin, Input } from "antd";
import StudentLayout from "@/components/layout/student/studentLayout";
import { formatDate } from "@/lib/helper/date";
import { sprintf } from "@/lib/utils/sprintf";
import { useApiGetStudentHomework, useApiStudentDoHomework } from "@/api/homework";

import {
    Calendar,
    CheckCircle2,
    CircleOff,
    Clock,
    Download,
    FileText,
    MessageCircle,
    Send,
    Star,
    User
} from 'lucide-react';
import FileViewer from "@/lib/helper/FileViewer";
import UploadCMP from "@/components/upload";
import ApiHandleSuccessMessage, { ApiHandleErrorMessage } from "@/lib/helper/apiHandleResultMessage";

function StudentHomeworkDetail(props) {
    const param = useParams();
    const [newResponse, setNewResponse] = useState('');
    const [selectedFile, setSelectedFile] = useState();

    const { data: lessonData, isPending, isSuccess } =
        useApiReadStudentLessonDetailRaw(param.id);

    const {
        data: homeworkData,
        isPendingGetHomework,
        isSuccessGetHomework,
        refetch
    } = useApiGetStudentHomework({ id: param.homeworkId });

    const { mutate: doHomework, isPending: isPendingDoHomework } =
        useApiStudentDoHomework();

    const handleSubmit = (e) => {
        e.preventDefault();

        const args = {
            id: param.homeworkId,
            takenunit_id: param.id,
            file_id: selectedFile,
            report: newResponse,
        };

        doHomework(args, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                setNewResponse('');
                setSelectedFile(null);
                refetch();
            },
            onError: (r) => {
                ApiHandleErrorMessage(r);
            }
        });
    };

    return (
        <>
            <StudentLayout
                pageLoading={isPending || isPendingGetHomework}
                pageTitle="تکلیف درس"
                backTo={sprintf(URLS.student.lessons.myLesson.detail, { id: param.id })}
            >
                <RenderIf condition={isPending}>
                    <Spin spinning={true} />
                </RenderIf>

                <div className="w-full mx-auto px-4 py-8">
                    <div className="rounded-2xl shadow-xl bg-white dark:bg-gray-900 overflow-hidden">

                        {/* HEADER */}
                        <div className="bg-gradient-to-l from-blue-600 to-cyan-600 dark:from-blue-800 dark:to-cyan-800 text-white px-8 py-6 rounded-t-xl">
                            <div className="flex items-start justify-between flex-wrap">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 opacity-90">
                                        <FileText className="w-5 h-5" />
                                        <span className="text-sm font-medium">تکلیف درس</span>
                                    </div>

                                    <h1 className="text-2xl font-bold mb-3">
                                        {homeworkData?.data?.data?.title}
                                    </h1>

                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                        <div className="flex items-center gap-1.5">
                                            <User className="w-4 h-4" />
                                            <span>
                                                {lessonData?.data?.data?.lesson?.teacher_detail?.firstname}{' '}
                                                <b>{lessonData?.data?.data?.lesson?.teacher_detail?.lastname}</b>
                                            </span>
                                        </div>

                                        <div className="h-4 w-px bg-white/30" />

                                        <div className="flex items-center gap-1.5">
                                            <Calendar className="w-4 h-4" />
                                            <span>
                                                تاریخ ارسال: {formatDate(homeworkData?.data?.data?.created_at)}
                                            </span>
                                        </div>

                                        <RenderIf condition={homeworkData?.data?.data?.date}>
                                            <div className="h-4 w-px bg-white/30" />
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-4 h-4" />
                                                <span>
                                                    مهلت: {formatDate(homeworkData?.data?.data?.date)}
                                                </span>
                                            </div>
                                        </RenderIf>

                                        <RenderIf condition={homeworkData?.data?.data?.score}>
                                            <div className="h-4 w-px bg-white/30" />
                                            <div className="flex items-center gap-1.5">
                                                <Star className="w-4 h-4" />
                                                <span>امتیاز: {homeworkData?.data?.data?.score}</span>
                                            </div>
                                        </RenderIf>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-8 py-6">
                            {/* LESSON INFO */}
                            <div className="mb-6">
                                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/60 rounded-xl px-4 py-3 mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                                            {lessonData?.data?.data?.lesson?.topic?.title}
                                        </span>
                                    </div>
                                    <span className="text-xs text-blue-700 dark:text-blue-300 mr-4">
                                        {lessonData?.data?.data?.lesson?.title}
                                    </span>
                                </div>
                            </div>

                            {/* HOMEWORK DESCRIPTION */}
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                                    <div className="w-1 h-5 bg-blue-500 rounded" />
                                    شرح تکلیف
                                </h2>

                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-800/70 rounded-xl p-5 border border-gray-100 dark:border-gray-700 whitespace-pre-wrap">
                                    {homeworkData?.data?.data?.description}
                                </p>
                            </div>

                            {/* ATTACHMENT */}
                            {homeworkData?.data?.data?.file && (
                                <div className="mb-8">
                                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                                        <div className="w-1 h-5 bg-blue-500 rounded" />
                                        فایل پیوست
                                    </h2>

                                    <FileViewer fileUrl={homeworkData?.data?.data?.file} />

                                    <a
                                        href={homeworkData?.data?.data?.file}
                                        className="inline-flex items-center gap-3 bg-gradient-to-l from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 dark:from-blue-600 dark:to-cyan-600 dark:hover:from-blue-500 dark:hover:to-cyan-500 text-white px-5 py-3 rounded-xl transition-all shadow-md hover:shadow-lg"
                                    >
                                        <Download className="w-5 h-5" />
                                        <span className="font-medium">دانلود فایل تکلیف</span>
                                    </a>
                                </div>
                            )}

                            {/* SUBMITTED ANSWERS */}
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                                    <div className="w-1 h-5 bg-blue-500 rounded" />
                                    پاسخ‌های ارسالی
                                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 mr-2">
                                        ({homeworkData?.data?.data?.reports?.length} پاسخ)
                                    </span>
                                </h2>

                                <div className="space-y-4">
                                    {homeworkData?.data?.data?.reports?.map((report) => (
                                        <div
                                            key={report.id}
                                            className="bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-800 dark:to-blue-950/30 rounded-xl p-5 border border-gray-200 dark:border-gray-700"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                                                    د
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                                                                پاسخ شما
                                                            </span>

                                                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                                                <Calendar className="w-3.5 h-3.5" />
                                                                {formatDate(report.created_at)}
                                                            </div>
                                                        </div>

                                                        <div
                                                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                                                report.status === 'accepted'
                                                                    ? 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300'
                                                                    : report.status === 'rejected'
                                                                        ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
                                                                        : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                                            }`}
                                                        >
                                                            <RenderIf condition={report.status === 'accepted'}>
                                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                                بررسی شده
                                                            </RenderIf>

                                                            <RenderIf condition={report.status === 'pending'}>
                                                                <Clock className="w-3.5 h-3.5" />
                                                                در انتظار بررسی
                                                            </RenderIf>

                                                            <RenderIf condition={report.status === 'rejected'}>
                                                                <CircleOff className="w-3.5 h-3.5" />
                                                                رد شده
                                                            </RenderIf>
                                                        </div>
                                                    </div>

                                                    <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed whitespace-pre-wrap">
                                                        {report?.report}
                                                    </p>

                                                    {report.file && (
                                                        <FileViewer fileUrl={report.file} />
                                                    )}

                                                    {report.advice && (
                                                        <div className="mt-4 rounded-lg p-4 border-r-4 border-teal-500 bg-teal-50/70 dark:bg-teal-950/30 shadow-sm">
                                                            <div className="flex items-start gap-3">
                                                                <MessageCircle className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />

                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                        <span className="font-semibold text-teal-700 dark:text-teal-300">
                                                                            پاسخ مربی
                                                                        </span>

                                                                        <RenderIf condition={report?.score}>
                                                                            <Star className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                                                                            <span className="text-xs text-gray-600 dark:text-gray-300">
                                                                                امتیاز کسب شده: {report.score}
                                                                            </span>
                                                                        </RenderIf>
                                                                    </div>

                                                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                                                        {report.advice}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* NEW RESPONSE */}
                            <div className="border-t-2 border-gray-100 dark:border-gray-700 pt-8">
                                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                                    <div className="w-1 h-5 bg-teal-500 rounded" />
                                    ارسال پاسخ جدید
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            متن پاسخ
                                        </label>

                                        <Input.TextArea
                                            value={newResponse}
                                            onChange={(e) => setNewResponse(e.target.value)}
                                            rows={5}
                                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 dark:focus:ring-teal-950/50 transition-all outline-none resize-none dark:!bg-gray-800 dark:!text-gray-100 dark:placeholder:!text-gray-500"
                                            placeholder="پاسخ خود را اینجا بنویسید..."
                                        />
                                    </div>

                                    <div>
                                        <UploadCMP
                                            afterUploadFn={(f) => {
                                                setSelectedFile(f?.file?.response?.data?.id);
                                            }}
                                            label="فایل پیوست (اختیاری)"
                                            dragMode={true}
                                        />
                                    </div>

                                    <div className="flex justify-end pt-2">
                                        <Button
                                            onClick={handleSubmit}
                                            loading={isPendingDoHomework}
                                            type="submit"
                                            htmlType="submit"
                                            className="flex items-center gap-2 !bg-gradient-to-l !from-teal-600 !to-cyan-600 hover:!from-teal-700 hover:!to-cyan-700 !text-white !border-0 px-8 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                        >
                                            <Send className="w-5 h-5" />
                                            ارسال پاسخ
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </StudentLayout>
        </>
    );
}

export default StudentHomeworkDetail;
